// docs/69 — every status the sim can set (`fk_grammar::StatusKind`), as the player sees it: its
// name, its colour, a little pixel icon (the same rows draw the board's badge and the HUD's chip),
// whether it helps or harms, and the note that says what it does. The rule words are `statusGloss`
// (04 §3.2), so the note never says something the sim doesn't do. A test pins the kinds to the
// Rust enum: a new status fails it until it has a look here and in `game/status/looks.ts`.
import type { StatusKind, StatusTag } from './types';
import { statusGloss } from './describe';

export type StatusTone = 'boon' | 'bane' | 'mixed' | 'mark';

export interface StatusInfo {
  name: string;
  /** Two letters, for the planning pills and screen readers' short lists. */
  abbr: string;
  colour: number;
  tone: StatusTone;
  /** 7×7 pixel glyph: '#' drawn, anything else empty. Inked (`glyphInk`) on the colour, in a dark rim. */
  icon: readonly string[];
  /** What it looks like on the board, in a few words (the status book lists it). */
  look: string;
}

export const STATUS_INFO: Record<StatusKind, StatusInfo> = {
  Guarded: { name: 'Guarded', abbr: 'Gd', colour: 0xa9bdd4, tone: 'boon', look: 'a steel shield shimmers before it',
    icon: ['#######', '#.....#', '#..#..#', '#.###.#', '.#.#.#.', '..#.#..', '...#...'] },
  Warded: { name: 'Warded', abbr: 'Wd', colour: 0x5ad1ff, tone: 'boon', look: 'a rune circle turns under it, motes orbit',
    icon: ['...#...', '..#.#..', '.#...#.', '#..#..#', '.#...#.', '..#.#..', '...#...'] },
  Blessed: { name: 'Blessed', abbr: 'Bl', colour: 0xfff0b8, tone: 'boon', look: 'a golden halo, motes of light rising',
    icon: ['...#...', '...#...', '..###..', '#######', '..###..', '...#...', '...#...'] },
  Marked: { name: 'Marked', abbr: 'Mk', colour: 0xff4d6a, tone: 'bane', look: 'a red reticle turns over it',
    icon: ['..###..', '.#...#.', '#.....#', '#..#..#', '#.....#', '.#...#.', '..###..'] },
  Hidden: { name: 'Hidden', abbr: 'Hd', colour: 0x8b98b8, tone: 'boon', look: 'veiled in a shimmer, wisps of smoke at its feet',
    icon: ['.......', '.......', '#.....#', '.#####.', '.#.#.#.', '.......', '.......'] },
  Slowed: { name: 'Slowed', abbr: 'Sl', colour: 0x6f8cff, tone: 'bane', look: 'tinted cold blue, slow ripples spread from its feet',
    icon: ['#######', '.#####.', '..###..', '...#...', '..#.#..', '.#...#.', '#######'] },
  Rooted: { name: 'Rooted', abbr: 'Rt', colour: 0x6fbf4a, tone: 'bane', look: 'roots coil up from the ground round its legs',
    icon: ['...#...', '...#...', '..###..', '.#.#.#.', '#..#..#', '.#.#.#.', '#..#..#'] },
  Burning: { name: 'Burning', abbr: 'Bu', colour: 0xff8a2a, tone: 'bane', look: 'flames lick up its body, embers fly',
    icon: ['...#...', '..##...', '..###..', '.####.#', '.#####.', '###.###', '.#####.'] },
  Weakened: { name: 'Weakened', abbr: 'Wk', colour: 0xa77ad6, tone: 'bane', look: 'washed grey-violet, a pall sinking off it',
    icon: ['..###..', '..###..', '..###..', '#######', '.#####.', '..###..', '...#...'] },
  Staggered: { name: 'Staggered', abbr: 'St', colour: 0xffe14d, tone: 'bane', look: 'stars circle its head as it sways',
    icon: ['.#.....', '###....', '.#...#.', '....###', '.#...#.', '###....', '.#.....'] },
  Stuck: { name: 'Stuck', abbr: 'Sk', colour: 0x9ccf3a, tone: 'bane', look: 'a sticky web holds its feet, goo drips',
    icon: ['...#...', '.#.#.#.', '..###..', '#######', '..###..', '.#.#.#.', '...#...'] },
  Bleeding: { name: 'Bleeding', abbr: 'Bd', colour: 0xe0303f, tone: 'bane', look: 'drops of blood fall and pool at its feet',
    icon: ['...#...', '..###..', '..###..', '.#####.', '.##.##.', '.#####.', '..###..'] },
  Downed: { name: 'Downed', abbr: 'Dn', colour: 0x9aa0aa, tone: 'bane', look: 'a faint heartbeat pulses round it',
    icon: ['.##.##.', '#######', '###.###', '.##.##.', '..#.#..', '...#...', '.......'] },
  Injured: { name: 'Injured', abbr: 'In', colour: 0xd9b48a, tone: 'bane', look: 'a bandage badge (it is carried between fights)',
    icon: ['..###..', '..#.#..', '###.###', '#.....#', '###.###', '..#.#..', '..###..'] },
  Frenzied: { name: 'Frenzied', abbr: 'Fz', colour: 0xff3b1f, tone: 'mixed', look: 'a red pulse of rage, steam off its head',
    icon: ['..#..#.', '.#..#..', '#..#..#', '..#..#.', '.#..#..', '#..#...', '.......'] },
  Arriving: { name: 'Arriving', abbr: 'Ar', colour: 0xff9a3c, tone: 'mark', look: 'a dashed ring turns at its feet, dust settles',
    icon: ['#.....#', '##...##', '.##.##.', '..###..', '...#...', '.......', '.......'] },
  Captive: { name: 'Captive', abbr: 'Cp', colour: 0xd8d8d8, tone: 'mark', look: 'bound with rope, a padlock over its head',
    icon: ['..###..', '.#...#.', '.#...#.', '#######', '#.....#', '#..#..#', '#######'] },
  Raised: { name: 'Raised', abbr: 'Rs', colour: 0xb48cff, tone: 'mark', look: 'violet wisps of the raising rise off it',
    icon: ['..###..', '.#####.', '##.#.##', '#######', '#######', '#######', '#.#.#.#'] },
  // docs/104 PD2: a paladin's Interpose — the magnitude is the paladin's unit id, never a number to show
  Shielded: { name: 'Shielded', abbr: 'Sh', colour: 0xf2c94c, tone: 'boon', look: 'a gold shield with a sun cross stands at its back, glinting; light motes rise',
    icon: ['.#####.', '#..#..#', '#.###.#', '#..#..#', '.#.#.#.', '..#.#..', '...#...'] },
  // docs/93 AQ8: shoved or dragged into water too deep for it — it thrashes, bobs and sinks a little
  Floundering: { name: 'Floundering', abbr: 'Fl', colour: 0x3f8fd6, tone: 'bane', look: 'thrashing in deep water, splashes and bubbles round it, bobbing as it sinks',
    icon: ['..###..', '..###..', '#..#..#', '.#####.', '.......', '#.#.#.#', '.#.#.#.'] },
  // docs/93 AQ7: a swimmer up out of the deep after a strike, open to sight and blows until it dives
  Surfaced: { name: 'Surfaced', abbr: 'Sf', colour: 0x5fd0c0, tone: 'mark', look: 'risen out of the water, water streaming off it',
    icon: ['......#', '.....##', '....###', '...####', '.......', '#.#.#.#', '.#.#.#.'] },
};

export const STATUS_KINDS = Object.keys(STATUS_INFO) as StatusKind[];

export const statusInfo = (kind: string): StatusInfo | undefined => STATUS_INFO[kind as StatusKind];

/** "turns" left as the HUD says it: "2 turns", "this turn", or "" (until cleared). */
export function turnsLeft(pulses: number | undefined): string {
  if (pulses === undefined) return '';
  return pulses <= 1 ? 'this turn' : `${pulses} turns`;
}

/** A unit's name by its id, for the statuses that name someone (Shielded by its paladin). */
export type UnitNamer = (unit: number) => string | undefined;

/** One status in a line: "Burning 2 (2 turns)"; docs/104 PD2: "Shielded by Odran (this turn)". */
export function statusLabel(t: StatusTag, nameOf?: UnitNamer): string {
  const info = statusInfo(t.kind);
  const by = t.kind === 'Shielded' ? nameOf?.(t.magnitude) : undefined;
  const name = `${info?.name ?? t.kind}${by ? ` by ${by}` : ''}`;
  const mag = t.magnitude > 0 && MAGNITUDE_SHOWN.has(t.kind) ? ` ${t.magnitude}` : '';
  const left = turnsLeft(t.pulses);
  return `${name}${mag}${left ? ` (${left})` : ''}`;
}

/** The statuses whose number is part of the rule (the others carry 0 or 1 for nothing). */
const MAGNITUDE_SHOWN: ReadonlySet<StatusKind> = new Set(['Guarded', 'Warded', 'Blessed', 'Marked', 'Slowed', 'Burning', 'Weakened', 'Frenzied']);

/** The note a status reads by: "Burning 2 (2 turns): takes 2 damage at the end of each turn." */
export function statusNote(t: StatusTag, nameOf?: UnitNamer): string {
  return `${statusLabel(t, nameOf)}: ${statusGloss(t.kind, t.magnitude)}.`;
}

/**
 * A unit's statuses in a line, for the hover chip: "Staggered (this turn), Bleeding (2 turns)". A
 * docs/66 mark says what it means ("Arriving: acts next turn…"), as it did before it was a badge.
 */
export function statusesLine(tags: readonly StatusTag[] | undefined, nameOf?: UnitNamer): string | undefined {
  const shown = shownStatuses(tags);
  return shown.length ? shown.map((t) => (STATUS_INFO[t.kind].tone === 'mark' ? `${t.kind}: ${statusGloss(t.kind, t.magnitude)}` : statusLabel(t, nameOf))).join(', ') : undefined;
}

/** The statuses a player reads: marks first, then the helpful, then the harmful. */
export function shownStatuses(tags: readonly StatusTag[] | undefined): StatusTag[] {
  const rank: Record<StatusTone, number> = { mark: 0, boon: 1, mixed: 2, bane: 3 };
  return (tags ?? []).filter((t) => statusInfo(t.kind)).sort((a, b) => rank[statusInfo(a.kind)!.tone] - rank[statusInfo(b.kind)!.tone]);
}

export const cssColour = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

/** The glyph's ink on a status's colour: dark on the pale ones (gold, steel, rope), white on the rest. */
export function glyphInk(colour: number): string {
  const r = (colour >> 16) & 255, g = (colour >> 8) & 255, b = colour & 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 165 ? '#1a1406' : '#ffffff';
}
